import {
  RepriceServiceDocument,
  RepriceServiceMutationResult,
  RepriceServiceMutationVariables,
} from "@graphql/api";
import varsFromRes from "@graphql/services_query_vars";
import { repricingVar } from "@graphql/vars/repricingVar";
import Session from "@lib/session";
import { getConfig } from "@lib/use_config";

if (global.window) global.window.repriceQueue = [];

// query[:calculationType] = recalc_entire_fare ? 0 : 1
// recalculate 0 (full pricing with new base fare comes back)   slow - initial pricing request
// regenerate 1 (service extras) - you give me fare components (with base not recalculated) fast

async function executeReprice(
  legNum: number,
  slowRecalculateEntireFare: boolean = false,
  secondTry: boolean = false,
): Promise<boolean> {
  let roundtrip = Session.getBooking().roundtrip;
  if (roundtrip === undefined) {
    const { data } = await getConfig(["defaultRoundTrip"]);
    roundtrip = data.config.defaultRoundTrip;
  }
  const servicesVars: RepriceServiceMutationVariables = varsFromRes(
    Session.getLeg(legNum),
    !!roundtrip,
    legNum,
    "full",
    slowRecalculateEntireFare,
  );

  const client = global.window.apolloClient;
  const result: RepriceServiceMutationResult = await client.mutate({
    mutation: RepriceServiceDocument,
    variables: servicesVars,
  });

  let leg = Session.getLeg(legNum);

  // Check for errors using nAPI's success field
  if (!result.data.repriceService.success) {
    if (secondTry) {
      // Clear times if reprice failed (e.g., no services available for this flight type)
      if (slowRecalculateEntireFare && leg.service) {
        leg.service.times = [];
        leg.service.selectedTime = undefined;
        Session.setLeg(legNum, leg);
      }
      return false;
    }

    // Error, try to reprice again.
    return await executeReprice(legNum, slowRecalculateEntireFare, true);
  }

  const newValues = result.data.repriceService.reprice;
  if (leg.service && newValues) {
    leg.service.bagCountMax = newValues.bagCountMax || leg.service.bagCountMax;
    leg.service.fareDisplay = newValues.fareDisplay;
    leg.service.gratuityFare = newValues.gratuityFare;
    leg.service.originalFare = newValues.originalFare;
    leg.service.fareComponents = newValues.fareComponents;
    leg.service.webSubfares = newValues.webSubfares;

    // Don't update payments if there's a queued reprice. We'll clobber the most recent value.
    if (!window.repriceQueue[legNum].queuedRegen && !window.repriceQueue[legNum].queuedRecalc)
      leg.payments = newValues.payments;

    if (slowRecalculateEntireFare && !leg.open) {
      leg.service.times = newValues.times;

      // Update pickup time if original pickup not available.
      const timeStillAvailable = newValues.times?.find(
        (t: be.ServiceTime) => t.pickupTime == leg.service?.selectedTime?.pickupTime,
      );
      if (!timeStillAvailable && newValues.times) {
        leg.service.selectedTime =
          newValues.times.find((t: be.ServiceTime) => t.suggested) || newValues.times[0];
        if (leg.service.selectedTime) leg.service.selectedTime.customerSelected = true;
      }
    }
  }
  Session.setLeg(legNum, leg);

  return true;
}

async function manageReprices(
  legNum: number,
  slowRecalculateEntireFare: boolean = false,
): Promise<boolean | "QUEUED"> {
  // Init reprice queue if needed.
  window.repriceQueue[legNum] ||= {};

  // If currently runninng a recalc/regen then queue the incoming.
  if (window.repriceQueue[legNum].currentlyRunningRequest) {
    if (slowRecalculateEntireFare) {
      window.repriceQueue[legNum].queuedRegen = true;
      window.repriceQueue[legNum].queuedRecalc = false;
    } else {
      window.repriceQueue[legNum].queuedRecalc = true;
    }
    return "QUEUED";
  }

  // Run the request
  window.repriceQueue[legNum].currentlyRunningRequest = new Date().getTime();
  const success = await executeReprice(legNum, slowRecalculateEntireFare);

  // If error and we don't have a queued regen then return error to the parent.
  if (!success && !window.repriceQueue[legNum].queuedRegen) {
    window.repriceQueue[legNum] = {};
    return true;
  }

  if (window.repriceQueue[legNum].queuedRegen) {
    // If we have a queued regen then execute it (and update the queue to reflect that)
    window.repriceQueue[legNum] = { queuedRecalc: window.repriceQueue[legNum].queuedRecalc };
    return await manageReprices(legNum, true);
  } else if (window.repriceQueue[legNum].queuedRecalc) {
    // Otherwise if we have a queued recalc then execute that.
    window.repriceQueue[legNum] = {};
    return await manageReprices(legNum, false);
  }

  window.repriceQueue[legNum] = {};
  return false;
}

export default async function reprice(
  legNum: number,
  slowRecalculateEntireFare: boolean = false,
): Promise<boolean | "QUEUED"> {
  repricingVar([true, new Date().getTime()]);

  const error = await manageReprices(legNum, slowRecalculateEntireFare);
  if (error != "QUEUED") repricingVar([false, new Date().getTime()]);
  return error;
}
